import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { aboutData, valueProposition } from '../../data/mock';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about-section"
      ref={sectionRef}
      className="py-24 lg:py-32 bg-stone-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Problem Statement */}
        <div
          className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-2xl md:text-3xl lg:text-4xl font-serif text-stone-800 leading-relaxed mb-6">
            {valueProposition.problem}
          </p>
          <p className="text-stone-600 text-lg leading-relaxed">
            {valueProposition.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <p className="text-xl md:text-2xl font-serif text-stone-800 leading-relaxed mb-6">
              {aboutData.title}
            </p>
            <p className="text-stone-600 leading-relaxed mb-6">
              {aboutData.description}
            </p>
            <p className="text-stone-600 leading-relaxed mb-8">
              {aboutData.secondaryDescription}
            </p>
            <Link
              to="/destinations"
              className="inline-flex items-center gap-2 text-stone-900 font-medium hover:gap-4 transition-all duration-300 group"
            >
              {aboutData.cta}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Image */}
          <div
            className={`relative transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={aboutData.image}
                alt="Venice canal dining"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Location Badge */}
            <div className="absolute bottom-6 left-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
              <MapPin className="w-4 h-4 text-amber-700" />
              <span className="text-sm font-medium text-stone-700">{aboutData.location}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
